import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_BASE, api } from "../../api/client";

export type AssertionKind =
  | "fires"
  | "fire_at"
  | "fire_window"
  | "value"
  | "param_at"
  | "report_contains"
  | "report_count"
  | "event"
  | "no_errors"
  | "golden";

export interface Assertion {
  kind: AssertionKind;
  name?: string | null;
  trigger?: string | null;
  label?: string | null;
  index?: number;
  report?: string | null;
  output?: string | null;
  filename?: string | null;
  mnemonic?: string | null;
  key?: string | null;
  event_kind?: string | null;
  count?: number | null;
  min?: number | null;
  max?: number | null;
  equals?: unknown;
  t?: number | null;
  start?: number | null;
  end?: number | null;
  length?: number | null;
  tol?: number;
  text?: string | null;
  regex?: string | null;
  absent?: boolean;
  ignore?: string[];
}

export interface Scenario {
  name: string;
  description?: string;
  flight: string;
  tail?: string | null;
  inputs?: Record<string, unknown>;
  fields?: Record<string, unknown>;
  golden?: string | null;
  tags?: string[];
  skip?: string | null;
  expect: Assertion[];
  matrix?: Record<string, unknown[]>;
  file?: string;
  source_index?: number;
  expanded?: string[];
}

export interface ScenarioFile {
  path: string;
  name: string;
  size: number;
  modified: number;
  scenarios: Scenario[];
  problems: { severity: string; message: string }[];
}

export interface Baseline {
  name: string;
  path: string;
  flight_id: string | null;
  fires: number;
  outputs: string[];
  captured: number;
}

export interface CheckResult {
  name: string;
  kind: AssertionKind;
  passed: boolean;
  message: string;
  expected: unknown;
  actual: unknown;
}

export interface Drift {
  kind: "fire" | "event" | "output" | "errors";
  status: "added" | "removed" | "changed";
  name: string;
  detail: string;
  diff: string;
}

export interface GoldenResult {
  name: string;
  exists: boolean;
  passed: boolean;
  compared_files: number;
  drift: Drift[];
  message: string;
}

export interface ScenarioResult {
  name: string;
  file: string | null;
  source_index: number | null;
  description: string;
  tags: string[];
  flight: string;
  flight_id: string | null;
  inputs: Record<string, unknown>;
  status: "passed" | "failed" | "skipped" | "error";
  passed: boolean;
  message: string;
  checks: CheckResult[];
  golden: GoldenResult | null;
  golden_name: string | null;
  baseline_updated: boolean;
  fires: { trigger: string; label: string; t: number; window: [number, number]; values: Record<string, unknown> }[];
  outputs: string[];
  errors: string[];
  elapsed_s: number;
}

export interface VerifyReport {
  project: string;
  passed: boolean;
  summary: { scenarios: number; passed: number; failed: number; skipped: number; error: number; checks: number; checks_failed: number };
  elapsed_s: number;
  results: ScenarioResult[];
  ran_at?: string;
  user?: string;
  update_golden?: boolean;
}

export interface TestsOverview {
  files: ScenarioFile[];
  baselines: Baseline[];
  pytest: { exists: boolean; generated: boolean; path: string };
  last: VerifyReport | null;
  assertion_kinds: AssertionKind[];
}

export const verifyKeys = {
  overview: (p: string) => ["tests", p] as const,
  golden: (p: string, name: string) => ["tests-golden", p, name] as const,
};

export const useTestsOverview = (projectId: string | null) =>
  useQuery({
    queryKey: verifyKeys.overview(projectId ?? ""),
    queryFn: () => api.get<TestsOverview>(`/projects/${projectId}/tests`),
    enabled: !!projectId,
  });

export const useGoldenDetail = (projectId: string | null, name: string | null) =>
  useQuery({
    queryKey: verifyKeys.golden(projectId ?? "", name ?? ""),
    queryFn: () =>
      api.get<{ name: string; summary: Record<string, unknown>; files: { name: string; size: number }[] }>(
        `/projects/${projectId}/tests/golden/${encodeURIComponent(name ?? "")}`,
      ),
    enabled: !!projectId && !!name,
  });

export function goldenFileUrl(projectId: string, name: string, filename: string): string {
  return `${API_BASE}/projects/${projectId}/tests/golden/${encodeURIComponent(name)}/files/${encodeURIComponent(filename)}`;
}

export function useRunTests(projectId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { only?: string[]; file?: string; tags?: string[]; update_golden?: boolean }) =>
      api.post<VerifyReport>(`/projects/${projectId}/tests/run`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: verifyKeys.overview(projectId ?? "") }),
  });
}

export function useSaveScenarioFile(projectId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ filename, content, scenarios }: { filename: string; content?: string; scenarios?: Scenario[] }) =>
      api.put<{ path: string; scenarios: string[]; expanded: number }>(`/projects/${projectId}/tests/scenarios/${encodeURIComponent(filename)}`, {
        content,
        scenarios,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: verifyKeys.overview(projectId ?? "") });
      qc.invalidateQueries({ queryKey: ["files", projectId] });
    },
  });
}

export function useDeleteScenarioFile(projectId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (filename: string) => api.del<void>(`/projects/${projectId}/tests/scenarios/${encodeURIComponent(filename)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: verifyKeys.overview(projectId ?? "") }),
  });
}

export function validateScenario(projectId: string, content: string) {
  return api.post<{ problems: { severity: string; line: number; col: number; message: string; code: string }[]; scenarios: Scenario[] }>(
    `/projects/${projectId}/tests/scenarios:validate`,
    { content },
  );
}

export function suggestScenario(projectId: string, body: { run_id: string; flight_id: string; name?: string; golden?: boolean; tol?: number }) {
  return api.post<{ scenario: Scenario; yaml: string }>(`/projects/${projectId}/tests/scenarios:suggest`, body);
}

export function useDeleteGolden(projectId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.del<void>(`/projects/${projectId}/tests/golden/${encodeURIComponent(name)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: verifyKeys.overview(projectId ?? "") }),
  });
}

export function useExportPytest(projectId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ written: string[]; pytest: TestsOverview["pytest"]; command: string }>(`/projects/${projectId}/tests/export-pytest`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: verifyKeys.overview(projectId ?? "") });
      qc.invalidateQueries({ queryKey: ["files", projectId] });
    },
  });
}
